import type { JSONContent } from "@tiptap/react";

import {
  ComposeEmailPayload,
  CreateDraftPayload,
  EmailContact,
  EmailFetchParams,
  EmailFolderCounts,
  EmailMessage,
  EmailThread,
  EmailThreadListItem,
  ForwardEmailPayload,
  MessageAttachmentUrls,
  ReplyEmailPayload,
  SendEmailResponse,
  ThreadActionResponse,
  UpdateDraftPayload,
} from "@/types/email";
import type { CursorPage } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export async function getEmailThread(
  threadId: string,
  scope: "provider" | "clinic" = "provider",
): Promise<EmailThread | null> {
  if (scope === "clinic") {
    return await requestData<EmailThread>(`email/threads/${threadId}`, "get", {
      searchParams: { scope },
    });
  }
  return await requestData<EmailThread>(`email/threads/${threadId}`, "get");
}

export async function getEmailThreads(
  params: EmailFetchParams = {},
): Promise<CursorPage<EmailThreadListItem> | null> {
  const folder = params.folder ? params.folder.toUpperCase() : undefined;

  return await requestData<CursorPage<EmailThreadListItem>>(
    "email/threads",
    "get",
    {
      searchParams: {
        folder,
        per_page: params.perPage,
        cursor: params.cursor,
        search: params.search,
        before_date: params.beforeDate,
        after_date: params.afterDate,
        scope: params.scope,
        triage: params.triage ? true : undefined,
        unread: params.unread ? true : undefined,
        starred: params.starred ? true : undefined,
        has_patient: params.hasPatient ? true : undefined,
        has_attachment: params.hasAttachment ? true : undefined,
        sent_by: params.sentBy || undefined,
        group_by_patient: params.groupByPatient ? true : undefined,
      },
    },
  );
}

export async function getClinicEmailThreads(
  params: Omit<EmailFetchParams, "scope"> = {},
): Promise<CursorPage<EmailThreadListItem> | null> {
  return await getEmailThreads({ ...params, scope: "clinic" });
}

export async function getClinicFolderCounts(): Promise<EmailFolderCounts | null> {
  return await requestData<EmailFolderCounts>("email/threads/counts", "get");
}

export async function markEmailThreadAsRead(
  threadId: string,
): Promise<{ success: boolean } | null> {
  return await requestData<{ success: boolean }>(
    `email/threads/${threadId}/read`,
    "post",
    undefined,
    false,
  );
}

export async function markEmailThreadAsUnread(
  threadId: string,
): Promise<ThreadActionResponse | null> {
  return await requestData<ThreadActionResponse>(
    `email/threads/${threadId}/unread`,
    "post",
    undefined,
    false,
  );
}

export async function toggleEmailThreadStar(
  threadId: string,
): Promise<ThreadActionResponse | null> {
  return await requestData<ThreadActionResponse>(
    `email/threads/${threadId}/star`,
    "post",
    undefined,
    false,
  );
}

export async function toggleEmailThreadArchive(
  threadId: string,
): Promise<ThreadActionResponse | null> {
  return await requestData<ThreadActionResponse>(
    `email/threads/${threadId}/archive`,
    "post",
    undefined,
    false,
  );
}

export async function getAttachmentDownloadUrl(
  attachmentId: string,
): Promise<{ signedUrl: string } | null> {
  return await requestData<{ signedUrl: string }>(
    `email/attachments/${attachmentId}/download`,
    "get",
  );
}

export async function getMessageAttachmentUrls(
  messageId: string,
): Promise<MessageAttachmentUrls | null> {
  return await requestData<MessageAttachmentUrls>(
    `email/messages/${messageId}/attachment-urls`,
    "get",
  );
}

export async function uploadInlineImage(
  file: File,
): Promise<{ signedUrl: string } | null> {
  const formData = new FormData();
  formData.append("file", file);
  return await requestData<{ signedUrl: string }>(
    "email/attachments/upload-inline",
    "post",
    { body: formData },
  );
}

export async function replyToThread(
  threadId: string,
  payload: ReplyEmailPayload,
  files?: File[],
): Promise<SendEmailResponse | null> {
  const formData = new FormData();
  if (payload.bodyHtml) formData.append("body_html", payload.bodyHtml);
  if (payload.bodyText) formData.append("body_text", payload.bodyText);
  if (payload.toOverride)
    formData.append("to_override", JSON.stringify(payload.toOverride));
  if (payload.cc?.length) formData.append("cc", JSON.stringify(payload.cc));
  if (payload.bcc?.length) formData.append("bcc", JSON.stringify(payload.bcc));
  if (payload.senderScope) formData.append("sender_scope", payload.senderScope);
  if (payload.fromLocalPart)
    formData.append("from_local_part", payload.fromLocalPart);
  if (payload.senderName) formData.append("sender_name", payload.senderName);
  for (const file of files ?? []) formData.append("files", file);
  return await requestData<SendEmailResponse>(
    `email/threads/${threadId}/reply`,
    "post",
    { body: formData },
    false,
  );
}

/**
 * `documentIds` is index-aligned with `files`: the source chart document for
 * any attachment seeded from one, `null` for everything else. The backend
 * copies the bytes to a fresh `email-attachments/` blob, so this is the only
 * record of which chart document was emailed — and what "was this sent?" reads.
 */
export async function composeEmail(
  payload: ComposeEmailPayload,
  files?: File[],
  documentIds?: (string | null)[],
): Promise<SendEmailResponse | null> {
  const formData = new FormData();
  formData.append("to", JSON.stringify(payload.to));
  formData.append("subject", payload.subject);
  if (payload.bodyHtml) formData.append("body_html", payload.bodyHtml);
  if (payload.bodyText) formData.append("body_text", payload.bodyText);
  if (payload.patientId) formData.append("patient_id", payload.patientId);
  if (payload.senderScope) formData.append("sender_scope", payload.senderScope);
  if (payload.cc?.length) formData.append("cc", JSON.stringify(payload.cc));
  if (payload.bcc?.length) formData.append("bcc", JSON.stringify(payload.bcc));
  if (payload.fromLocalPart)
    formData.append("from_local_part", payload.fromLocalPart);
  if (payload.senderName) formData.append("sender_name", payload.senderName);
  for (const file of files ?? []) formData.append("files", file);
  if (documentIds?.some(Boolean))
    formData.append("document_ids", JSON.stringify(documentIds));
  return await requestData<SendEmailResponse>(
    "email/compose",
    "post",
    { body: formData },
    false,
  );
}

export async function forwardEmail(
  threadId: string,
  payload: ForwardEmailPayload,
  files?: File[],
): Promise<SendEmailResponse | null> {
  const formData = new FormData();
  formData.append("to", JSON.stringify(payload.to));
  if (payload.bodyHtml) formData.append("body_html", payload.bodyHtml);
  if (payload.bodyText) formData.append("body_text", payload.bodyText);
  if (payload.cc?.length) formData.append("cc", JSON.stringify(payload.cc));
  if (payload.bcc?.length) formData.append("bcc", JSON.stringify(payload.bcc));
  if (payload.senderScope) formData.append("sender_scope", payload.senderScope);
  if (payload.fromLocalPart)
    formData.append("from_local_part", payload.fromLocalPart);
  if (payload.senderName) formData.append("sender_name", payload.senderName);
  if (payload.messageId) formData.append("message_id", payload.messageId);
  if (payload.forwardAttachmentIds?.length)
    formData.append(
      "forward_attachment_ids",
      JSON.stringify(payload.forwardAttachmentIds),
    );
  for (const file of files ?? []) formData.append("files", file);
  return await requestData<SendEmailResponse>(
    `email/threads/${threadId}/forward`,
    "post",
    { body: formData },
    false,
  );
}

export async function createDraft(
  payload: CreateDraftPayload,
): Promise<EmailMessage | null> {
  // Backend returns the full draft (EmailMessageResponse), so the caller can
  // read the new draft's id/threadId and keep updating the same draft.
  return await requestData<EmailMessage>("email/drafts", "post", {
    json: payload,
  });
}

export async function updateDraft(
  draftId: string,
  payload: UpdateDraftPayload,
): Promise<EmailMessage | null> {
  return await requestData<EmailMessage>(`email/drafts/${draftId}`, "put", {
    json: payload,
  });
}

export async function deleteDraft(
  draftId: string,
): Promise<{ success: boolean } | null> {
  return await requestData<{ success: boolean }>(
    `email/drafts/${draftId}`,
    "delete",
  );
}

export async function sendDraft(
  draftId: string,
): Promise<SendEmailResponse | null> {
  return await requestData<SendEmailResponse>(
    `email/drafts/${draftId}/send`,
    "post",
  );
}

export interface SaveEmailSignaturePayload {
  templateJson: JSONContent;
}

export interface EmailSignatureResponse {
  templateJson: JSONContent | null;
}

export async function getEmailSignature(): Promise<EmailSignatureResponse | null> {
  return await requestData<EmailSignatureResponse>("email/signature", "get");
}

export async function saveEmailSignature(
  payload: SaveEmailSignaturePayload,
): Promise<{ success: boolean } | null> {
  return await requestData<{ success: boolean }>("email/signature", "put", {
    json: payload,
  });
}

export interface SaveEmailHeaderPayload {
  templateJson: JSONContent;
}

export interface EmailHeaderResponse {
  templateJson: JSONContent | null;
}

export async function getEmailHeader(): Promise<EmailHeaderResponse | null> {
  return await requestData<EmailHeaderResponse>("email/header", "get");
}

export async function saveEmailHeader(
  payload: SaveEmailHeaderPayload,
): Promise<{ success: boolean } | null> {
  return await requestData<{ success: boolean }>("email/header", "put", {
    json: payload,
  });
}

export async function searchEmailContacts(
  searchTerm: string,
): Promise<EmailContact[] | null> {
  return await requestData<EmailContact[]>("email/contacts/search", "get", {
    searchParams: { search_term: searchTerm },
  });
}

export async function submitEmailFeedback(message: string): Promise<null> {
  return await requestData("email/feedback", "post", {
    json: { message },
  });
}
